import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLocalization } from "@/context/localization-context";
import { Ago, EmptyBlock, SectionCard } from "../../components/primitives";
import { formatCount } from "../../format";
import type { BbBranch, BbSection } from "../../types";
import { BranchFlags, InlineStat, ProblemCount } from "../pipeline/shared";
import { OutcomeChip } from "./bits";

/** outcomes every branch has in plenty; the table lists the rest */
const ROUTINE = new Set(["captured", "in_progress"]);

function OtherOutcomes({ branch }: { branch: BbBranch }) {
  const entries = Object.entries(branch.outcomes)
    .filter(([outcome, n]) => !ROUTINE.has(outcome) && n > 0)
    .sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <span className="text-muted-foreground">–</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([outcome, n]) => (
        <span key={outcome} className="inline-flex items-center gap-1">
          <OutcomeChip outcome={outcome} />
          <span className="font-mono text-[11px] tabular-nums">{formatCount(n)}</span>
        </span>
      ))}
    </div>
  );
}

/** Logged webhooks per branch with a camera: orders, item updates, packing reports and what they fired. */
export function BbBranchesCard({ data }: { data: BbSection }) {
  const { t } = useLocalization();
  const branches = data.branches;
  return (
    <SectionCard title={t("monitoring.bb.branches.title")} description={t("monitoring.bb.branches.description")} contentClassName="p-0">
      {branches.length === 0 ? (
        <div className="p-4">
          <EmptyBlock>{t("monitoring.bb.branches.empty")}</EmptyBlock>
        </div>
      ) : (
        <>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-8">{t("monitoring.bb.branches.col.branch")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.bb.branches.col.orders")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.bb.branches.col.items")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.bb.branches.col.statuses")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.bb.branches.col.packed")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.bb.branches.col.captured")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.bb.branches.col.missing")}</TableHead>
                  <TableHead className="h-8">{t("monitoring.bb.branches.col.outcomes")}</TableHead>
                  <TableHead className="h-8 text-right">{t("monitoring.bb.branches.col.last")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branches.map((branch) => (
                  <TableRow key={branch.id ?? "unknown"}>
                    <TableCell className="py-2 align-top">
                      <div className="text-sm font-medium">{branch.name}</div>
                      <BranchFlags acquisition={branch.enable_acquisition} bbTrigger={branch.enable_bb_trigger} />
                    </TableCell>
                    <TableCell className="py-2 text-right align-top tabular-nums">{formatCount(branch.orders)}</TableCell>
                    <TableCell className="py-2 text-right align-top tabular-nums">{formatCount(branch.item_webhooks)}</TableCell>
                    <TableCell className="py-2 text-right align-top tabular-nums">{formatCount(branch.status_webhooks)}</TableCell>
                    <TableCell className="py-2 text-right align-top tabular-nums">{formatCount(branch.packed_items)}</TableCell>
                    <TableCell className="py-2 text-right align-top tabular-nums">{formatCount(branch.captured_items)}</TableCell>
                    <TableCell className="py-2 text-right align-top">
                      <ProblemCount value={branch.packed_not_captured} />
                    </TableCell>
                    <TableCell className="max-w-[22rem] py-2 align-top">
                      <OtherOutcomes branch={branch} />
                    </TableCell>
                    <TableCell className="py-2 text-right align-top text-xs">
                      <Ago ts={branch.last_at} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="divide-y divide-border/60 md:hidden">
            {branches.map((branch) => (
              <li key={branch.id ?? "unknown"} className="flex flex-col gap-1.5 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="break-words text-sm font-medium">{branch.name}</div>
                    <BranchFlags acquisition={branch.enable_acquisition} bbTrigger={branch.enable_bb_trigger} />
                  </div>
                  <Ago ts={branch.last_at} className="shrink-0 text-xs" />
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <InlineStat label={t("monitoring.bb.branches.col.orders")}>{formatCount(branch.orders)}</InlineStat>
                  <InlineStat label={t("monitoring.bb.branches.col.items")}>{formatCount(branch.item_webhooks)}</InlineStat>
                  <InlineStat label={t("monitoring.bb.branches.col.packed")}>{formatCount(branch.packed_items)}</InlineStat>
                  <InlineStat label={t("monitoring.bb.branches.col.captured")}>{formatCount(branch.captured_items)}</InlineStat>
                  <InlineStat label={t("monitoring.bb.branches.col.missing")}>
                    <ProblemCount value={branch.packed_not_captured} />
                  </InlineStat>
                  <InlineStat label={t("monitoring.bb.branches.col.statuses")}>{formatCount(branch.status_webhooks)}</InlineStat>
                </div>
                <OtherOutcomes branch={branch} />
              </li>
            ))}
          </ul>
        </>
      )}
    </SectionCard>
  );
}

/** Branches that send orders with photos off: only that they are there and how much they send. */
export function BbOtherBranchesCard({ data }: { data: BbSection }) {
  const { t } = useLocalization();
  if (!data.other_branches.length) return null;
  return (
    <SectionCard
      title={t("monitoring.bb.other.title")}
      description={t("monitoring.bb.other.description", { total: data.other_branches_total })}
      contentClassName="p-0"
    >
      <ul className="max-h-[22rem] divide-y divide-border/60 overflow-y-auto">
        {data.other_branches.map((branch) => (
          <li key={branch.id ?? "unknown"} className="flex items-center justify-between gap-3 px-4 py-2 text-xs">
            <span className="min-w-0 break-words">{branch.name || branch.id}</span>
            <span className="flex shrink-0 items-center gap-3 text-muted-foreground">
              <span className="tabular-nums">{t("monitoring.bb.other.orders", { orders: formatCount(branch.orders) })}</span>
              <Ago ts={branch.last_at} />
            </span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}
